import {
	Component,
	effect,
	OnDestroy,
	Type
}                                 from '@angular/core';
import { Title }                  from '@angular/platform-browser';
import {
	ActivatedRoute,
	Event,
	NavigationEnd,
	Router
}                                 from '@angular/router';
import { Profile }                from '@app/core/services/authorization/keycloak-service-wrapper';
import { OpenApiCheckerService }  from '@app/core/services/open-api-checker.service';
import { CurrentUserService }     from '@app/shared/services/current-user.service';
import { LoggerService }          from '@app/shared/services/logger.service';
import { AnalyticsMatomoService } from '@core/services/analytics-matomo.service';
import { BrandService }           from '@core/services/brand/brand.service';
import { TranslationService }     from '@core/services/translation.service';
import { ToasterService }         from '@shared/services/toaster.service';
import { UiService }              from '@shared/services/ui.service';
import { Subject }                from 'rxjs';
import {
	filter,
	map,
	skipWhile,
	startWith,
	take,
	takeUntil
}                                 from 'rxjs/operators';

@Component( {
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: [ './app.component.scss' ]
} )
export class AppComponent implements OnDestroy {

	private readonly _appTitle: string;
	private _i18nTitlePath: string;
	private _ngUnsubscribe: Subject<void> = new Subject();

	constructor(
		private readonly _activatedRoute: ActivatedRoute,
		private readonly _analyticsMatomoService: AnalyticsMatomoService,
		private readonly _brandService: BrandService,
		private readonly _currentUserService: CurrentUserService,
		private readonly _logger: LoggerService,
		private readonly _openApiCheck: OpenApiCheckerService,
		private readonly _router: Router,
		private readonly _titleService: Title,
		private readonly _toasterService: ToasterService,
		private readonly _translationService: TranslationService,
		private readonly _uiService: UiService
	) {
		this._openApiCheck.runDefaultCheck();
		this._appTitle = this._brandService.appTitle;
		this.setTitle( null );
		this._translationService.setupLanguage( this._toasterService );

		effect(
			() => {
				if ( this._translationService.translationServiceInitializedSignal() ) {
					this.subscribeToEvents();
				}
			}
		);
	}

	private subscribeToEvents(): void {
		let previousRouteComponentName: string;

		// get and set currentUsers language preference
		this._currentUserService.getProfile$()
		.pipe(
			skipWhile( ( profile: Profile ) => profile === null ),
			take( 1 )
		)
		.subscribe( ( profile: Profile ) => {
			this._logger.debug( 'language getProfile$ subscribe: ', profile );
			this._translationService.setUserLocale( profile.attributes?.locale );
		} );


		// language switch event
		this._uiService.languageHasChanged$
		.pipe( takeUntil( this._ngUnsubscribe ) )
		.subscribe( () => {
			if ( this._i18nTitlePath ) {
				this.setTitle( this._i18nTitlePath );
			}
		} );


		// route change event
		this._router?.events
		.pipe(
			takeUntil( this._ngUnsubscribe ),
			filter( ( routerEvent: Event ) => routerEvent instanceof NavigationEnd ),
			startWith( this._router ),
			map( () => {
				let child = this._activatedRoute?.firstChild;

				// stops further processing if no child
				if ( !child ) { return; }

				while ( child.firstChild ) {
					child = child.firstChild;
				}

				// scroll to 0 only on page component change
				const componentName = (
					child.component as Type<any>
				)?.name;

				if ( previousRouteComponentName !== componentName ) {
					previousRouteComponentName = componentName;
					window.scrollTo( 0, 0 );
				}


				// get title
				const title = child.snapshot?.data?.title;
				if ( title ) {
					this._i18nTitlePath = title;
					return title;
				}

				this._i18nTitlePath = null;
				return null;
			} )
		)
		.subscribe( ( title: string ) => {
			this.setTitle( title );

			// track usages
			this._analyticsMatomoService.trackAfterRouterEvent();
		} );
	}

	private setTitle( titlePath: string ): void {
		if ( !titlePath ) {
			this._titleService.setTitle( this._appTitle );
			return;
		}

		let translatedTitle = this._translationService.getI18nString(
			false,
			null,
			`title.${ titlePath }`,
			true,
			null
		);

		translatedTitle = translatedTitle ? `${ translatedTitle } | ${ this._appTitle }` : this._appTitle;
		this._titleService.setTitle( translatedTitle );
	}

	ngOnDestroy() {
		this._ngUnsubscribe.next();
		this._ngUnsubscribe.complete();
	}
}
